/**
 * PostInfo Component for LOOPINO AI Mobile
 * Shows post information header with scheduling, platform, counter, AI regeneration, and approval button
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getFirstMediaUrl, isVideoFile } from '../../../utils/mediaHelpers.js';
import Skeleton from '../../effects/Skeleton';
import useLanguage from '../../../hooks/useLanguage';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';

// Social platform icons
const PlatformIcon = ({ platform, size = 16 }) => {
  const icons = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="currentColor" strokeWidth="2"/>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="currentColor" strokeWidth="2"/>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 8C18.5259 8 20.5259 10 20.5259 12.5V19H16.5259V12.5C16.5259 11.5 15.5259 11 14.5259 11C13.5259 11 12.5259 11.5 12.5259 12.5V19H8.52588V8H12.5259V9.5C13.5259 8.5 14.5259 8 16 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="2" y="9" width="4" height="12" stroke="currentColor" strokeWidth="2"/>
        <circle cx="4" cy="4" r="2" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    x: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4L20 20M20 4L4 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    )
  };
  
  return icons[platform] || icons.instagram;
};

// Schedule icon
const ScheduleIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Campaign icon for counter
const CampaignIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2"/>
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

// Check icon for approval
const CheckIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// X icon for non-approved posts
const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Edit Icon component
const EditIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const DeleteIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18 6v13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// AI Icon - using provided icon-ai.svg  
const AIIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.56791 18.8123V17.9563H1.71194C1.23925 17.9563 0.856061 17.573 0.855972 17.1003C0.855972 16.6276 1.2392 16.2444 1.71194 16.2444H2.56791V15.3884C2.56791 14.9157 2.95119 14.5325 3.42389 14.5324C3.89666 14.5324 4.27986 14.9156 4.27986 15.3884V16.2444H5.13583C5.6086 16.2444 5.9918 16.6276 5.9918 17.1003C5.99171 17.573 5.60855 17.9563 5.13583 17.9563H4.27986V18.8123C4.27983 19.285 3.89664 19.6683 3.42389 19.6683C2.95121 19.6682 2.56794 19.285 2.56791 18.8123ZM10.4767 4.80819C10.6996 4.77883 10.927 4.79315 11.1455 4.85199H11.1446C11.5422 4.95852 11.8004 5.23185 11.9627 5.47207C12.1242 5.71114 12.2481 6.00066 12.3529 6.29619C12.5623 6.88697 12.7703 7.72584 13.0287 8.7586L13.1272 9.12687C13.1277 9.1282 13.1278 9.12959 13.1282 9.13085C13.1298 9.13146 13.1315 9.13219 13.1332 9.13284C13.2028 9.15873 13.3021 9.18814 13.4955 9.24332L14.7028 9.59467C15.0616 9.70418 15.3747 9.80788 15.6344 9.91118C15.8954 10.015 16.1552 10.1383 16.3729 10.2984C16.5953 10.4619 16.832 10.7075 16.9343 11.0667L16.9632 11.1822C17.0206 11.4541 17.0108 11.7369 16.9343 12.0053C16.832 12.3646 16.5953 12.6102 16.3729 12.7737C16.1552 12.9337 15.8954 13.0571 15.6344 13.1609C15.3747 13.2642 15.0616 13.3679 14.7028 13.4774L13.4955 13.8287C13.3017 13.884 13.2026 13.9134 13.1332 13.9392C13.1316 13.9398 13.1297 13.9396 13.1282 13.9402C13.1277 13.9417 13.1277 13.9436 13.1272 13.9452C13.1037 14.0159 13.0776 14.1158 13.0287 14.3125V14.3135C12.7703 15.3462 12.5623 16.1851 12.3529 16.7759C12.2482 17.0712 12.124 17.36 11.9627 17.599C11.8004 17.8393 11.5424 18.1135 11.1446 18.2201L11.1436 18.2191C10.8524 18.2974 10.5448 18.2978 10.2537 18.2191V18.2181C9.85746 18.1108 9.59965 17.839 9.43758 17.599C9.27623 17.36 9.15315 17.0703 9.04842 16.7749C8.83905 16.1842 8.62996 15.3461 8.3716 14.3135V14.3125C8.32287 14.1171 8.29666 14.0169 8.27306 13.9462C8.27238 13.9441 8.27171 13.9421 8.27107 13.9402C8.26992 13.9398 8.26831 13.9397 8.26709 13.9392C8.19746 13.9134 8.09824 13.8838 7.9048 13.8287H7.9038C7.01716 13.5751 6.28619 13.3673 5.76686 13.1609C5.50565 13.057 5.24523 12.9337 5.02734 12.7737C4.80497 12.6104 4.56776 12.3654 4.46499 12.0063V12.0053C4.37762 11.6987 4.37762 11.3734 4.46499 11.0667V11.0657L4.50978 10.9364C4.62696 10.6467 4.83277 10.4413 5.02734 10.2984C5.24523 10.1383 5.50565 10.015 5.76686 9.91118C6.28619 9.70471 7.01714 9.49701 7.9038 9.24332L8.26709 9.13284C8.26847 9.13233 8.26978 9.13134 8.27107 9.13085C8.27152 9.12954 8.2726 9.12826 8.27306 9.12687C8.29659 9.05618 8.32263 8.95638 8.3716 8.7596V8.7586L8.72792 7.36118C8.83852 6.94875 8.94375 6.5915 9.04842 6.29619C9.15315 6.00074 9.27615 5.71108 9.43758 5.47207C9.59972 5.23206 9.85767 4.95878 10.2547 4.85199L10.4767 4.80819ZM10.6618 6.8685C10.4867 7.36268 10.3008 8.10258 10.0328 9.17365C9.96005 9.46604 9.87841 9.86432 9.63366 10.1819L9.63167 10.1849C9.55326 10.2858 9.46375 10.3776 9.36493 10.4586L9.36393 10.4576C9.05572 10.7115 8.65922 10.8071 8.37359 10.8886L8.37459 10.8896C7.44827 11.1546 6.81882 11.3347 6.39888 11.5017C6.37093 11.5128 6.34557 11.5253 6.32125 11.5355C6.34567 11.5459 6.37079 11.5592 6.39888 11.5704C6.81882 11.7373 7.44826 11.9174 8.37459 12.1825H8.37359C8.62903 12.2552 8.96143 12.3366 9.24648 12.5249L9.36493 12.6125L9.36791 12.6154L9.50925 12.7468C9.55377 12.7931 9.59654 12.8411 9.63565 12.8921H9.63466C9.87752 13.2082 9.95977 13.6057 10.0328 13.8984L10.3821 15.267C10.4841 15.6477 10.5742 15.9565 10.6618 16.2036C10.6741 16.2383 10.6882 16.2699 10.6996 16.3001C10.7111 16.2698 10.7261 16.2384 10.7385 16.2036C10.9136 15.7094 11.0995 14.9695 11.3675 13.8984C11.4402 13.6061 11.522 13.2078 11.7666 12.8902L11.7686 12.8872L11.895 12.7428C11.9381 12.6986 11.9837 12.6568 12.0314 12.6174L12.1508 12.5279C12.4376 12.3366 12.7736 12.2544 13.0257 12.1825L14.2042 11.8391C14.5295 11.74 14.7917 11.6538 15.0014 11.5704C15.0291 11.5593 15.0539 11.5457 15.078 11.5355C15.054 11.5254 15.029 11.5127 15.0014 11.5017C14.7916 11.4183 14.5296 11.3311 14.2042 11.232L13.0257 10.8896C12.7349 10.8066 12.3427 10.7116 12.0354 10.4596L12.0324 10.4566C11.9345 10.3755 11.8462 10.2836 11.7686 10.1829V10.1839C11.5836 9.94538 11.4913 9.65879 11.4262 9.40854L11.3675 9.17365C11.0995 8.10255 10.9136 7.36267 10.7385 6.8685C10.726 6.83333 10.7112 6.80151 10.6996 6.77095C10.6881 6.80138 10.6742 6.8335 10.6618 6.8685ZM2.13993 8.96762V7.68366H0.855972C0.3832 7.68366 0 7.30046 0 6.82769C7.73956e-05 6.35498 0.383248 5.97172 0.855972 5.97172H2.13993V4.68776C2.13993 4.21503 2.52319 3.83186 2.9959 3.83179C3.46867 3.83179 3.85187 4.21499 3.85187 4.68776V5.97172H5.13583C5.60855 5.97172 5.99172 6.35498 5.9918 6.82769C5.9918 7.30046 5.6086 7.68366 5.13583 7.68366H3.85187V8.96762C3.85187 9.44039 3.46867 9.82359 2.9959 9.82359C2.52319 9.82351 2.13993 9.44034 2.13993 8.96762Z" fill="currentColor"/>
  </svg>
);

const CampaignNameTicker = ({ name, onClick }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [animationDuration, setAnimationDuration] = useState(6);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let frameId = null;

    const measure = () => {
      const containerEl = containerRef.current;
      const contentEl = contentRef.current;

      if (!containerEl || !contentEl) {
        frameId = window.requestAnimationFrame(measure);
        return;
      }

      const overflow = Math.max(contentEl.scrollWidth - containerEl.clientWidth, 0);
      setHasOverflow(overflow > 0);
      setScrollDistance((prev) => {
        if (Math.abs(prev - overflow) < 0.5) {
          return prev;
        }
        return overflow;
      });

      if (overflow > 0) {
        const nextDuration = Math.max(4, Math.min(12, 4 + overflow / 20));
        setAnimationDuration((prev) => {
          if (Math.abs(prev - nextDuration) < 0.1) {
            return prev;
          }
          return nextDuration;
        });
      }
    };

    frameId = window.requestAnimationFrame(measure);

    const handleResize = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(measure);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [name]);

  return (
    <span
      ref={containerRef}
      className={`mobile-post-campaign-name${hasOverflow ? ' has-overflow' : ''}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <span
        ref={contentRef}
        className="mobile-post-campaign-name-content"
        style={hasOverflow ? {
          '--scroll-distance': `${scrollDistance}px`,
          '--scroll-duration': `${animationDuration}s`,
          '--scroll-extra': '2ch'
        } : undefined}
      >
        {name}
      </span>
    </span>
  );
};

const PostInfo = ({
  post,
  currentIndex = 0,
  totalPosts = 1,
  thumbnailImage,
  onApprove,
  onAIRegenerate,
  onEditPost,
  onReject, // Aggiunta funzione per gestire la rimozione dell'approvazione
  onDeletePost,
  isApproved = false,
  isProcessing = false,
  isRegenerating = false,
  campaignName,
  allPosts = [], // Array di tutti i post per calcolare il counter per campagna
  onCampaignClick, // Callback per navigare alla pagina della campagna
  onThumbnailClick, // Callback per navigare al calendario cliccando il thumbnail
  isLoading = false
}) => {
  const { t, language } = useLanguage();
  const { confirm } = useMobileAlert();
  const resolvedLocale = useMemo(() => {
    if (language) {
      try {
        // Validate locale; Intl throws for invalid values
        new Intl.DateTimeFormat(language);
        return language;
      } catch (error) {
        console.warn('[PostInfo] Falling back to it locale', { language, error });
      }
    }
    return 'it';
  }, [language]);
  const [localApproved, setLocalApproved] = useState(isApproved);
  const [localProcessing, setLocalProcessing] = useState(false);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  // Sync local approval state when prop changes
  useEffect(() => {
    setLocalApproved(isApproved);
  }, [isApproved]);


  // Helper function to generate thumbnail from video first frame
  const generateVideoThumbnail = (videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      
      const handleLoadedData = () => {
        try {
          // Set canvas size to match video (thumbnail proportions)
          const aspectRatio = video.videoWidth / video.videoHeight;
          canvas.width = 80; // Fixed thumbnail width
          canvas.height = 80 / aspectRatio;
          
          // Draw first frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to data URL
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(thumbnailDataUrl);
        } catch (error) {
          reject(error);
        } finally {
          // Cleanup
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('error', handleError);
          video.src = '';
          video.load();
        }
      };
      
      const handleError = (error) => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        reject(new Error('Failed to load video for thumbnail generation'));
      };
      
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      
      // Load video and seek to first frame
      video.src = videoUrl;
      video.currentTime = 0.1; // Slightly after start to ensure frame is loaded
    });
  };

  // Generate thumbnail from video if needed
  useEffect(() => {
    if (thumbnailImage && isVideoFile(thumbnailImage) && !generatedThumbnail) {
      setThumbnailLoading(true);
      
      generateVideoThumbnail(thumbnailImage)
        .then(thumbnail => {
          setGeneratedThumbnail(thumbnail);
        })
        .catch(error => {
          console.error('[PostInfo] Error generating video thumbnail:', error);
          setGeneratedThumbnail(null);
        })
        .finally(() => {
          setThumbnailLoading(false);
        });
    }
  }, [thumbnailImage, generatedThumbnail]);

  // Reset generated thumbnail when thumbnailImage changes
  useEffect(() => {
    if (thumbnailImage && !isVideoFile(thumbnailImage)) {
      setGeneratedThumbnail(null);
    }
  }, [thumbnailImage]);

  // Loading skeleton UI
  if (isLoading) {
    return (
      <div className="mobile-post-info-skeleton" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ position: 'relative' }}>
          <Skeleton variant="rounded" width="80px" height="80px" shimmer className="mr-2" />
        </div>
        <div style={{ flex: 1 }}>
          <Skeleton width="60%" height="14px" shimmer className="mb-6" />
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <Skeleton variant="rounded" width="90px" height="12px" shimmer />
            <Skeleton variant="rounded" width="70px" height="12px" shimmer />
            <Skeleton variant="rounded" width="60px" height="12px" shimmer />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Skeleton variant="rounded" width="100px" height="28px" shimmer />
            <Skeleton variant="rounded" width="100px" height="28px" shimmer />
          </div>
        </div>
      </div>
    );
  }

  // Format schedule time
  const formatSchedule = (scheduledTime) => {
    if (!scheduledTime) return t('postReview.postInfo.schedule.none');

    try {
      const date = new Date(scheduledTime);
      if (Number.isNaN(date.getTime())) {
        return t('postReview.postInfo.schedule.invalid');
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const postDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const timeString = new Intl.DateTimeFormat(resolvedLocale, {
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);

      const isToday = postDate.getTime() === today.getTime();
      const isTomorrow = postDate.getTime() === tomorrow.getTime();

      if (isToday) {
        return t('postReview.postInfo.schedule.today', { time: timeString });
      }

      if (isTomorrow) {
        return t('postReview.postInfo.schedule.tomorrow', { time: timeString });
      }

      return timeString;
    } catch (error) {
      console.error('[PostInfo] Error formatting schedule:', error);
      return t('postReview.postInfo.schedule.invalid');
    }
  };

  // Format date for thumbnail overlay
  const formatThumbnailDate = (scheduledTime) => {
    if (!scheduledTime) return { day: '', month: '' };

    try {
      const date = new Date(scheduledTime);
      if (Number.isNaN(date.getTime())) return { day: '', month: '' };

      const day = new Intl.DateTimeFormat(resolvedLocale, { day: 'numeric' }).format(date);
      const month = new Intl.DateTimeFormat(resolvedLocale, { month: 'long' }).format(date);

      return { day, month };
    } catch (error) {
      console.error('[PostInfo] Error formatting thumbnail date:', error);
      return { day: '', month: '' };
    }
  };

  // Get platform display name
  const getPlatformName = (platform) => {
    const names = {
      instagram: 'Instagram',
      facebook: 'Facebook',
      linkedin: 'LinkedIn',
      x: 'X (Twitter)'
    };
    return names[platform] || platform || t('postReview.postInfo.platforms.default');
  };

  // Extract post data safely
  const scheduledTime = post?.scheduled_time || post?.schedule || post?.schedulingInfo?.scheduled_time;
  const platform = post?.platform || post?.social_platform || 'instagram';
  const postTitle = post?.title || post?.topic || post?.caption || `Post ${currentIndex + 1}`;

  // Check if post is in the past
  const isPostInPast = () => {
    if (!scheduledTime) return false;

    try {
      const postDate = new Date(scheduledTime);
      const now = new Date();
      return postDate < now;
    } catch (error) {
      console.error('[PostInfo] Error checking if post is in past:', error);
      return false;
    }
  };

  // Check post publication status from API data
  const getPostStatus = () => {
    const statusRaw = post?.status;

    let normalizedStatus = '';
    if (typeof statusRaw === 'string') {
      normalizedStatus = statusRaw.toLowerCase();
    } else if (statusRaw && typeof statusRaw === 'object') {
      const candidate = statusRaw.value || statusRaw.current || statusRaw.status || statusRaw.state || '';
      normalizedStatus = candidate ? candidate.toString().toLowerCase() : '';
    } else if (statusRaw !== undefined && statusRaw !== null) {
      normalizedStatus = String(statusRaw).toLowerCase();
    }

    const isExplicitlyPosted = post?.posted === true;
    const isPublished = normalizedStatus === 'published' || normalizedStatus === 'posted' || isExplicitlyPosted;

    if (isPublished) {
      return { type: 'posted', label: t('postReview.postInfo.status.posted'), color: 'success' };
    }

    if (normalizedStatus === 'failed') {
      return { type: 'not-posted', label: t('postReview.postInfo.status.failed'), color: 'error' };
    }

    if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
      return { type: 'not-posted', label: t('postReview.postInfo.status.cancelled'), color: 'error' };
    }

    if (normalizedStatus === 'publishing' || normalizedStatus === 'processing') {
      return { type: 'publishing', label: t('postReview.postInfo.status.publishing'), color: 'info' };
    }

    if (normalizedStatus === 'queued') {
      return { type: 'scheduled', label: t('postReview.postInfo.status.approved'), color: 'approved' };
    }

    if (normalizedStatus === 'scheduled') {
      return { type: 'scheduled', label: t('postReview.postInfo.status.approved'), color: 'approved' };
    }

    if (isPostInPast()) {
      return { type: 'not-posted', label: t('postReview.postInfo.status.failed'), color: 'error' };
    }

    return { type: 'draft', label: t('postReview.postInfo.status.pending'), color: 'pending' };
  };

  const postStatus = getPostStatus();
  const postIsInPast = isPostInPast();

  const buttonVisualState = (() => {
    if (postStatus.type === 'posted') return 'posted';
    if (postStatus.type === 'not-posted') return 'not-posted';
    if (postStatus.type === 'scheduled') return 'approved';
    if (postStatus.type === 'publishing') return 'approved';
    if (localApproved) return 'approved';
    if (postIsInPast) return 'expired';
    return 'pending';
  })();

  const approvalAriaLabel = (() => {
    if (postStatus.type === 'posted') {
      return t('postReview.postInfo.actions.aria.posted');
    }

    if (postStatus.type === 'not-posted') {
      if (postStatus.label === t('postReview.postInfo.status.cancelled')) {
        return t('postReview.postInfo.actions.aria.cancelled');
      }
      return t('postReview.postInfo.actions.aria.notPublished');
    }

    if (postStatus.type === 'publishing') {
      return t('postReview.postInfo.actions.aria.publishing');
    }

    if (postIsInPast) {
      return t('postReview.postInfo.actions.aria.expired');
    }

    return localApproved
      ? t('postReview.postInfo.actions.aria.unapprove')
      : t('postReview.postInfo.actions.aria.approve');
  })();

  const isApprovalDisabled =
    localProcessing ||
    postIsInPast ||
    postStatus.type === 'posted' ||
    postStatus.type === 'not-posted' ||
    postStatus.type === 'publishing';

  const isSecondaryActionsDisabled =
    isProcessing ||
    localProcessing ||
    postStatus.type === 'posted';

  // Handle approval toggle
  const handleApproval = async () => {
    if (
      localProcessing ||
      isProcessing ||
      postIsInPast ||
      postStatus.type === 'posted' ||
      postStatus.type === 'not-posted' ||
      postStatus.type === 'publishing'
    ) {
      return;
    }

    setLocalProcessing(true);

    try {
      if (localApproved) {
        // Se è già approvato, rimuovi l'approvazione
        
        if (onReject) {
          await onReject(post);
        }
        
        setLocalApproved(false);
        
      } else {
        // Se non è approvato, approvalo
        
        if (onApprove) {
          await onApprove(post);
        }
        
        setLocalApproved(true);
      }
      
    } catch (error) {
      console.error('[PostInfo] Error toggling approval:', error);
    } finally {
      setLocalProcessing(false);
    }
  };

  // Handle AI regeneration
  const handleAIClick = async () => {
    if (isRegenerating || isSecondaryActionsDisabled) return;
    
    try {
      
      if (onAIRegenerate) {
        await onAIRegenerate(post, 'full');
      }
      
    } catch (error) {
      console.error('[PostInfo] Error with AI regeneration:', error);
    }
  };

  // Handle edit post
  const handleEditClick = () => {
    if (isSecondaryActionsDisabled) return;
    
    console.log('[PostInfo] Edit button clicked for post:', post);
    console.log('[PostInfo] Post data structure:', {
      id: post?.id,
      content: post?.content,
      description: post?.description,
      images: post?.images,
      media_urls: post?.media_urls,
      platform: post?.platform,
      scheduled_time: post?.scheduled_time
    });
    
    try {
      if (onEditPost) {
        console.log('[PostInfo] Calling onEditPost with:', post);
        onEditPost(post);
      } else {
        console.warn('[PostInfo] No onEditPost handler provided');
      }
      
    } catch (error) {
      console.error('[PostInfo] Error opening edit page:', error);
    }
  };

  const handleDeleteClick = async () => {
    if (isSecondaryActionsDisabled) return;

    const confirmed = await confirm({
      title: t('postReview.postInfo.confirm.delete'),
      variant: 'warning',
      confirmText: t('postReview.postInfo.actions.delete', { defaultValue: 'Elimina' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
    });

    if (!confirmed) return;

    if (onDeletePost) {
      onDeletePost(post);
    }
  };

  const handleCampaignClick = (event) => {
    event.stopPropagation();
    if (onCampaignClick) {
      onCampaignClick(post);
    }
  };

  // Calcola il counter per campagna
  const getCampaignCounter = () => {
    if (!post || allPosts.length === 0) {
      // Fallback al counter totale se non abbiamo i dati
      return { current: currentIndex + 1, total: totalPosts };
    }

    // Estrai l'ID della campagna del post corrente e normalizzalo
    const rawCampaignId = post?.campaignId || post?.campaign_id;

    // Converti l'ID in stringa per confronto sicuro (potrebbe essere un oggetto MongoDB ObjectId)
    const currentCampaignId = rawCampaignId?.$oid ||
                              (typeof rawCampaignId === 'object' ? JSON.stringify(rawCampaignId) : rawCampaignId);

    if (!currentCampaignId) {
      // Se il post non ha campaign ID, usa il counter totale
      return { current: currentIndex + 1, total: totalPosts };
    }

    // Filtra i post della stessa campagna, normalizzando gli ID per il confronto
    const campaignPosts = allPosts.filter(p => {
      const rawId = p?.campaignId || p?.campaign_id;
      const normalizedId = rawId?.$oid || (typeof rawId === 'object' ? JSON.stringify(rawId) : rawId);
      return normalizedId === currentCampaignId;
    });

    // Trova la posizione del post corrente nella sua campagna
    const positionInCampaign = campaignPosts.findIndex(p =>
      (p?.id || p?._id) === (post?.id || post?._id)
    );

    return {
      current: positionInCampaign >= 0 ? positionInCampaign + 1 : 1,
      total: campaignPosts.length
    };
  };

  const campaignCounter = getCampaignCounter();

  // Use provided thumbnail or extract from post using consistent logic
  const originalThumbnail = thumbnailImage || getFirstMediaUrl(post);

  // Debug log to verify thumbnail consistency
  if (process.env.NODE_ENV === 'development') {
    console.log('[PostInfo] Post thumbnail:', {
      postId: post?.id,
      providedThumbnail: thumbnailImage,
      media_urls: post?.media_urls,
      images: post?.images,
      post_image_url: post?.post_image_url,
      image_url: post?.image_url,
      selectedThumbnail: originalThumbnail
    });
  }

  // Use generated thumbnail if available, otherwise use original
  const finalThumbnail = generatedThumbnail ||
                        (originalThumbnail && !isVideoFile(originalThumbnail) ? originalThumbnail : null);

  return (
    <div className="mobile-post-info-header">
      {/* Left Side - Thumbnail + Meta */}
      <div className="mobile-post-info-content">
        {/* Post Thumbnail */}
        <div
          className="mobile-post-thumbnail-container"
          onClick={(e) => {
            e.stopPropagation();
            if (onThumbnailClick) {
              onThumbnailClick();
            }
          }}
          style={{ cursor: onThumbnailClick ? 'pointer' : 'default' }}
        >
          {thumbnailLoading ? (
            // Loading placeholder for video thumbnail generation
            <div className="mobile-post-thumbnail-loading">
              <div className="mobile-thumbnail-spinner"></div>
            </div>
          ) : (
            finalThumbnail ? (
              <img
                src={finalThumbnail}
                alt={t('postReview.postInfo.images.thumbnailAlt')}
                className="mobile-post-thumbnail"
                onError={(e) => {
                  // Hide only the image if it fails to load, keep container and overlay visible
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              // Fallback block when no thumbnail is available
              <div className="mobile-post-thumbnail-fallback" aria-hidden="true" />
            )
          )}
          <div className="mobile-thumbnail-date-overlay">
            <div className="mobile-thumbnail-day">
              {formatThumbnailDate(scheduledTime).day}
            </div>
            <div className="mobile-thumbnail-month">
              {formatThumbnailDate(scheduledTime).month}
            </div>
          </div>
        </div>

        {/* Post Meta Information */}
        <div className="mobile-post-meta">
          {/* Schedule Info */}
          <div className="mobile-post-schedule">
            <ScheduleIcon size={14} />
            <span>{formatSchedule(scheduledTime)}</span>
          </div>

          {/* Platform Info */}
          <div className="mobile-post-platform">
            <PlatformIcon platform={platform} size={14} />
            <span>{getPlatformName(platform)}</span>
          </div>

          {/* Post Counter */}
          <div className="mobile-post-counter">
            <CampaignIcon size={14} />
            <span>
              {campaignName ? (
                <>
                  <CampaignNameTicker
                    name={campaignName}
                    onClick={handleCampaignClick}
                  /> {campaignCounter.current}/{campaignCounter.total}
                </>
              ) : (
                `${campaignCounter.current}/${campaignCounter.total}`
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Right Side - Actions */}
      <div className="mobile-post-actions">
        {/* Approval Button - Full Width on Top */}
        <button
          className={`mobile-approval-button ${buttonVisualState} ${localProcessing ? 'processing' : ''}`}
          onClick={handleApproval}
          disabled={isApprovalDisabled}
          aria-label={approvalAriaLabel}
        >
          {localProcessing ? (
            <div className="mobile-approval-spinner" />
          ) : postStatus.type === 'posted' ? (
            <>
              <CheckIcon size={14} />
              <span>{postStatus.label}</span>
            </>
          ) : postStatus.type === 'not-posted' ? (
            <>
              <XIcon size={14} />
              <span>{postStatus.label}</span>
            </>
          ) : postStatus.type === 'publishing' ? (
            <>
              <div className="mobile-approval-spinner" />
              <span>{postStatus.label}</span>
            </>
          ) : localApproved ? (
            t('postReview.postInfo.actions.approved')
          ) : (
            t('postReview.postInfo.actions.approve')
          )}
        </button>

        {/* Secondary Actions Row */}
        <div className="mobile-secondary-actions">
          {/* Edit Post Button */}
          <button
            className="btn-base btn-sm btn-secondary"
            onClick={handleEditClick}
            disabled={isSecondaryActionsDisabled}
            aria-label={t('postReview.postInfo.secondary.edit')}
          >
            <EditIcon size={16} />
          </button>

          {/* AI Regeneration Button */}
          <button
            className={`btn-base btn-sm btn-secondary ${isRegenerating ? 'regenerating' : ''}`}
            onClick={handleAIClick}
            disabled={isRegenerating || isSecondaryActionsDisabled}
            aria-label={t('postReview.postInfo.secondary.regenerate')}
          >
            <AIIcon size={16} />
          </button>

          <button
            type="button"
            className="btn-base btn-sm btn-secondary mobile-delete-button"
            onClick={handleDeleteClick}
            disabled={isSecondaryActionsDisabled}
            aria-label={t('postReview.postInfo.secondary.delete')}
          >
            <DeleteIcon size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostInfo;
